import { Component, Inject, OnDestroy, OnInit, inject } from '@angular/core';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { InteractionStatus, InteractionType, PromptValue, RedirectRequest } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { hasMsalAccount, resolveLoginRedirectUri } from 'src/app/utils/msal-auth.util';

@Component({
  selector: 'app-logged-out',
  templateUrl: './logged-out.component.html',
  styleUrls: ['./logged-out.component.scss']
})
export class LoggedOutComponent implements OnInit, OnDestroy {
  private readonly msal = inject(MsalService);
  private readonly msalBroadcast = inject(MsalBroadcastService);
  private readonly destroy$ = new Subject<void>();

  /** Evita flash de "Sesión cerrada" mientras redirigimos al home con sesión activa. */
  ready = false;
  loginError: string | null = null;

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration
  ) {}

  ngOnInit(): void {
    this.msalBroadcast.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.tryEnterAppIfAuthenticated()) {
          return;
        }
        this.ready = true;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  login(): void {
    this.loginError = null;
    const redirectUri = resolveLoginRedirectUri();

    if (this.tryEnterAppIfAuthenticated()) {
      return;
    }

    const request: RedirectRequest = {
      ...(this.msalGuardConfig.authRequest as RedirectRequest),
      scopes: [...environment.apiConfig.scopes],
      prompt: PromptValue.LOGIN,
      redirectUri,
      extraQueryParameters: {
        ...(this.msalGuardConfig.authRequest as RedirectRequest)?.extraQueryParameters,
        ui_locales: environment.msalConfig.auth.ui_locales || 'es'
      }
    };

    const showLoginError = (): void => {
      this.loginError = 'No se pudo iniciar sesión. Inténtalo nuevamente.';
    };

    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      this.msal.loginPopup(request).subscribe({
        error: (err) => {
          console.error('MSAL loginPopup failed', err);
          showLoginError();
        }
      });
      return;
    }

    this.msal.loginRedirect(request).subscribe({
      error: (err) => {
        console.error('MSAL loginRedirect failed', err);
        showLoginError();
      }
    });
  }

  private tryEnterAppIfAuthenticated(): boolean {
    const accounts = this.msal.instance.getAllAccounts();
    if (!hasMsalAccount(accounts)) {
      return false;
    }

    const account =
      this.msal.instance.getActiveAccount() ?? accounts[0] ?? null;
    if (account) {
      this.msal.instance.setActiveAccount(account);
    }

    window.location.replace(`${resolveLoginRedirectUri()}/`);
    return true;
  }
}
